import { useState, useEffect, useCallback, createContext, useContext } from "react";

interface GameStats {
  score: number;
  attempts: number;
  streak: number;
}

const STORAGE_KEY = "apprendre_stats";

function loadStats(): GameStats {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { score: 0, attempts: 0, streak: 0 };
    const parsed = JSON.parse(raw);
    return {
      score: Number(parsed.score) || 0,
      attempts: Number(parsed.attempts) || 0,
      streak: Number(parsed.streak) || 0,
    };
  } catch {
    return { score: 0, attempts: 0, streak: 0 };
  }
}

function saveStats(stats: GameStats) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
}

// Migration from old keys
function migrateOldStats() {
  const oldScore = localStorage.getItem("apprendre_score");
  const oldAttempts = localStorage.getItem("apprendre_attempts");
  if (oldScore !== null || oldAttempts !== null) {
    const current = loadStats();
    const newScore = Math.max(current.score, Number(oldScore || 0));
    const newAttempts = Math.max(current.attempts, Number(oldAttempts || 0));
    saveStats({ score: newScore, attempts: newAttempts, streak: current.streak });
    localStorage.removeItem("apprendre_score");
    localStorage.removeItem("apprendre_attempts");
  }
}

// ---- Context ----
interface GameStatsContextValue {
  score: number;
  attempts: number;
  streak: number;
  addAttempt: () => void;
  addWin: () => void;
  resetStats: () => void;
}

const GameStatsContext = createContext<GameStatsContextValue | null>(null);

export function GameStatsProvider({ children }: { children: React.ReactNode }) {
  const [stats, setStats] = useState<GameStats>(() => {
    migrateOldStats();
    return loadStats();
  });

  // Sync across tabs / components via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setStats(loadStats());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const addAttempt = useCallback(() => {
    setStats((prev) => {
      const next = { ...prev, attempts: prev.attempts + 1 };
      saveStats(next);
      return next;
    });
  }, []);

  const addWin = useCallback(() => {
    setStats((prev) => {
      const next = { ...prev, score: prev.score + 1, streak: prev.streak + 1 };
      saveStats(next);
      return next;
    });
  }, []);

  const resetStats = useCallback(() => {
    const next: GameStats = { score: 0, attempts: 0, streak: 0 };
    saveStats(next);
    setStats(next);
  }, []);

  const value: GameStatsContextValue = {
    score: stats.score,
    attempts: stats.attempts,
    streak: stats.streak,
    addAttempt,
    addWin,
    resetStats,
  };

  return (
    <GameStatsContext.Provider value={value}>
      {children}
    </GameStatsContext.Provider>
  );
}

export function useGameStats(): GameStatsContextValue {
  const ctx = useContext(GameStatsContext);
  if (!ctx) {
    throw new Error("useGameStats must be used within a GameStatsProvider");
  }
  return ctx;
}